import { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  AlertCircle, Search, Plus, Eye, XCircle, CheckCircle, 
  RotateCcw, AlertTriangle, FileCheck, X, RefreshCw, ChevronDown
} from 'lucide-react';

// Searchable Dropdown Component
function SearchableDropdown({ label, value, options, onChange, placeholder, displayField, valueField, required, disabled, testId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  const selectedOption = options.find(opt => opt[valueField] === value);
  const displayValue = selectedOption ? selectedOption[displayField] : '';
  
  const filteredOptions = options.filter(opt => 
    opt[displayField]?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2.5 border rounded-xl text-sm text-left flex items-center justify-between transition ${
          disabled ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
            : 'border-gray-200 hover:border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100'
        }`}
        data-testid={testId}
      >
        <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>{displayValue || placeholder}</span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">No results found</div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => { onChange(opt[valueField], opt); setIsOpen(false); setSearchTerm(''); }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-red-50 transition ${
                    opt[valueField] === value ? 'bg-red-100 text-red-800 font-medium' : 'text-gray-700'
                  }`}
                >
                  {opt[displayField]}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Rejections() {
  const { API } = useContext(AppContext);
  const [rejections, setRejections] = useState([]);
  const [qaRecords, setQaRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedRejection, setSelectedRejection] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);

  // Form state for new rejection
  const [formData, setFormData] = useState({
    qaId: '',
    grnNo: '',
    material: '',
    supplier: '',
    quantity: '',
    unit: 'kg',
    reason: '',
    resolution: '',
    remarks: ''
  });

  useEffect(() => {
    fetchRejections();
    fetchQARecords();
  }, [statusFilter]);

  const fetchRejections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stores/rejections`, {
        params: { status: statusFilter },
        
      });
      setRejections(response.data);
    } catch (error) {
      console.error('Error fetching rejections:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQARecords = async () => {
    try {
      // Fetch QA records that failed (for creating rejections)
      const response = await axios.get(`${API}/stores/qa`, {
        params: { status: 'Failed' },
        
      });
      setQaRecords(response.data || []);
    } catch (error) {
      console.error('Error fetching QA records:', error);
    }
  };

  const handleQASelect = (qaId, qa) => {
    setFormData({
      ...formData,
      qaId: qaId,
      grnNo: qa.grnNo || '',
      material: qa.material || '',
      supplier: qa.supplier || '',
      quantity: qa.quantity || '',
      unit: qa.unit || 'kg'
    });
  };

  const handleSaveRejection = async () => {
    setSaveStatus('saving');
    try {
      const rejectionData = {
        ...formData,
        date: new Date().toISOString().split('T')[0],
        status: 'Pending Resolution',
        hasImage: false
      };
      
      await axios.post(`${API}/stores/rejections`, rejectionData);
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus(null);
        setShowAddModal(false);
        fetchRejections();
        resetForm();
      }, 1500);
    } catch (error) {
      console.error('Error saving rejection:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleResolve = async (resolution) => {
    if (!selectedRejection) return;
    
    try {
      await axios.put(`${API}/stores/rejections/${selectedRejection.id}/resolve`, {
        resolution: resolution,
        resolvedDate: new Date().toISOString().split('T')[0]
      });
      
      setShowResolveModal(false);
      setSelectedRejection(null);
      fetchRejections();
    } catch (error) {
      console.error('Error resolving rejection:', error);
      alert('Failed to update resolution');
    }
  };

  const resetForm = () => {
    setFormData({
      qaId: '',
      grnNo: '',
      material: '',
      supplier: '',
      quantity: '',
      unit: 'kg',
      reason: '',
      resolution: '',
      remarks: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending Resolution': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Rejected & Returned': return 'bg-red-100 text-red-700 border-red-200';
      case 'Accepted Under Deviation': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Accepted': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending Resolution': return <AlertCircle size={14} />;
      case 'Rejected & Returned': return <RotateCcw size={14} />;
      case 'Accepted Under Deviation': return <AlertTriangle size={14} />;
      case 'Accepted': return <CheckCircle size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  const filteredData = rejections.filter(item =>
    (item.material?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.grnNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.supplier?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const stats = {
    total: rejections.length,
    pendingResolution: rejections.filter(r => r.status === 'Pending Resolution').length,
    rejectedReturned: rejections.filter(r => r.status === 'Rejected & Returned').length,
    acceptedDeviation: rejections.filter(r => r.status === 'Accepted Under Deviation').length,
    accepted: rejections.filter(r => r.status === 'Accepted').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-500 border-t-transparent"></div>
        <span className="ml-3 text-gray-600">Loading rejections data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="rejections-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg">
            <XCircle className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">QA Rejections</h1>
            <p className="text-sm text-gray-500">Track rejected materials from QA and their resolution</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search material, GRN, supplier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-48"
              data-testid="search-input"
            />
          </div>

          <button
            onClick={fetchRejections}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={16} className="text-gray-600" />
          </button>

          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-lg hover:from-red-600 hover:to-rose-600 transition text-sm font-medium"
            data-testid="add-rejection-btn"
          >
            <Plus size={16} />
            <span>Log Rejection</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total Rejections</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1 mb-1">
            <AlertCircle size={14} className="opacity-80" />
            <p className="text-xs opacity-80">Pending Resolution</p>
          </div>
          <p className="text-2xl font-bold">{stats.pendingResolution}</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1 mb-1">
            <RotateCcw size={14} className="opacity-80" />
            <p className="text-xs opacity-80">Rejected & Returned</p>
          </div>
          <p className="text-2xl font-bold">{stats.rejectedReturned}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1 mb-1">
            <AlertTriangle size={14} className="opacity-80" />
            <p className="text-xs opacity-80">Under Deviation</p>
          </div>
          <p className="text-2xl font-bold">{stats.acceptedDeviation}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1 mb-1">
            <CheckCircle size={14} className="opacity-80" />
            <p className="text-xs opacity-80">Accepted</p>
          </div>
          <p className="text-2xl font-bold">{stats.accepted}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        {['all', 'Pending Resolution', 'Rejected & Returned', 'Accepted Under Deviation', 'Accepted'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              statusFilter === status
                ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? 'All' : status}
          </button>
        ))}
      </div>

      {/* Rejections Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-red-50 to-rose-50">
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <AlertCircle size={16} className="text-red-500" />
            Rejection Records - Resolution Tracking
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Rejection ID</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Date</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">GRN No</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Material</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Supplier</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700">Qty</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Reason</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">Resolution</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No rejection records found
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-red-50/50 transition-colors" data-testid={`rejection-row-${item.id}`}>
                    <td className="px-3 py-3 font-mono text-red-600 font-medium">{item.id}</td>
                    <td className="px-3 py-3 text-gray-600">{item.date}</td>
                    <td className="px-3 py-3 font-mono text-blue-600">{item.grnNo}</td>
                    <td className="px-3 py-3 font-medium text-gray-900">{item.material}</td>
                    <td className="px-3 py-3 text-gray-600">{item.supplier}</td>
                    <td className="px-3 py-3 text-right font-semibold">{item.quantity} {item.unit}</td>
                    <td className="px-3 py-3 text-gray-600 max-w-xs truncate" title={item.reason}>{item.reason}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${getStatusColor(item.status)}`}>
                        {getStatusIcon(item.status)}
                        {item.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      {item.status === 'Pending Resolution' ? (
                        <button
                          onClick={() => { setSelectedRejection(item); setShowResolveModal(true); }}
                          className="px-2 py-1 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition text-[10px] font-medium"
                          data-testid={`resolve-btn-${item.id}`}
                        >
                          Resolve
                        </button>
                      ) : (
                        <button
                          onClick={() => { setSelectedRejection(item); setShowResolveModal(true); }}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded-lg transition"
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
        <h4 className="font-semibold mb-2">Resolution Options:</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
          <p className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded font-medium">Rejected & Returned</span>
            Material sent back to supplier
          </p>
          <p className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded font-medium">Accepted Under Deviation</span>
            Used with documented deviation
          </p>
          <p className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">Accepted</span>
            Re-inspected and approved
          </p>
        </div>
      </div>

      {/* Add Rejection Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <XCircle className="text-red-500" size={20} />
                <h2 className="font-bold text-lg">Log New Rejection</h2>
              </div>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* QA Record Selection */}
              <SearchableDropdown
                label="Select QA Record (Failed)"
                value={formData.qaId}
                options={qaRecords.map(qa => ({ ...qa, displayText: `${qa.id} - ${qa.material} (${qa.grnNo})` }))}
                onChange={handleQASelect}
                placeholder="Search and select failed QA record..."
                displayField="displayText"
                valueField="id"
                required={true}
                testId="qa-record-dropdown"
              />

              {/* Auto-filled from QA */}
              {formData.qaId && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-gray-500">GRN:</span> <span className="font-medium text-blue-600">{formData.grnNo}</span></div>
                    <div><span className="text-gray-500">Material:</span> <span className="font-medium">{formData.material}</span></div>
                    <div><span className="text-gray-500">Supplier:</span> <span className="font-medium">{formData.supplier}</span></div>
                    <div><span className="text-gray-500">Qty:</span> <span className="font-medium">{formData.quantity} {formData.unit}</span></div>
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Rejection Reason <span className="text-red-500">*</span></label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Describe why the material was rejected..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-red-400 outline-none resize-none"
                  data-testid="rejection-reason"
                />
              </div>

              {/* Additional Remarks */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Additional Remarks</label>
                <input
                  type="text"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Any additional notes..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-red-400 outline-none"
                />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm">
                Cancel
              </button>
              <button
                onClick={handleSaveRejection}
                disabled={saveStatus === 'saving' || !formData.qaId || !formData.reason}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  saveStatus === 'saved' ? 'bg-green-500 text-white' : 
                  saveStatus === 'error' ? 'bg-red-500 text-white' :
                  'bg-gradient-to-r from-red-500 to-rose-500 text-white hover:from-red-600 hover:to-rose-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid="save-rejection-btn"
              >
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Log Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && selectedRejection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileCheck className="text-red-500" size={20} />
                <h2 className="font-bold text-lg">Resolve Rejection</h2>
              </div>
              <button onClick={() => { setShowResolveModal(false); setSelectedRejection(null); }} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Rejection Details */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-red-600 mb-2">{selectedRejection.id}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-500">Material:</span> <span className="font-medium">{selectedRejection.material}</span></div>
                  <div><span className="text-gray-500">Supplier:</span> <span className="font-medium">{selectedRejection.supplier}</span></div>
                  <div><span className="text-gray-500">Qty:</span> <span className="font-medium">{selectedRejection.quantity} {selectedRejection.unit}</span></div>
                  <div><span className="text-gray-500">GRN:</span> <span className="font-medium text-blue-600">{selectedRejection.grnNo}</span></div>
                </div>
                <p className="mt-2 text-xs"><span className="text-gray-500">Reason:</span> {selectedRejection.reason}</p>
              </div>

              {selectedRejection.status === 'Pending Resolution' ? (
                <>
                  <p className="text-sm font-medium text-gray-700">Select Resolution:</p>
                  
                  {/* Resolution Options */}
                  <div className="space-y-2">
                    <button
                      onClick={() => handleResolve('Rejected & Returned')}
                      className="w-full p-3 border-2 border-red-200 rounded-xl hover:bg-red-50 hover:border-red-400 transition text-left group"
                      data-testid="resolve-rejected-returned"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg group-hover:bg-red-200 transition">
                          <RotateCcw size={20} className="text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-red-700">Rejected & Returned</p>
                          <p className="text-xs text-gray-500">Material sent back to supplier for replacement/credit</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => handleResolve('Accepted Under Deviation')}
                      className="w-full p-3 border-2 border-yellow-200 rounded-xl hover:bg-yellow-50 hover:border-yellow-400 transition text-left group"
                      data-testid="resolve-accepted-deviation"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-100 rounded-lg group-hover:bg-yellow-200 transition">
                          <AlertTriangle size={20} className="text-yellow-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-yellow-700">Accepted Under Deviation</p>
                          <p className="text-xs text-gray-500">Material used with documented deviation approval</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => handleResolve('Accepted')}
                      className="w-full p-3 border-2 border-green-200 rounded-xl hover:bg-green-50 hover:border-green-400 transition text-left group"
                      data-testid="resolve-accepted"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition">
                          <CheckCircle size={20} className="text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-green-700">Accepted</p>
                          <p className="text-xs text-gray-500">Re-inspected and found acceptable for use</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(selectedRejection.status)}`}>
                    {getStatusIcon(selectedRejection.status)}
                    {selectedRejection.status}
                  </span>
                  {selectedRejection.resolvedDate && (
                    <p className="text-xs text-gray-500 mt-2">Resolved on: {selectedRejection.resolvedDate}</p>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => { setShowResolveModal(false); setSelectedRejection(null); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Rejections;
